import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { authAPI } from '../api/client';
import BrandLogo from '../components/BrandLogo';

type Phase = 'verifying' | 'invalid' | 'ready' | 'submitting' | 'done';

export default function ResetPassword() {
  const { token = '' } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('verifying');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await authAPI.verifyPasswordResetToken(token);
        if (!active) return;
        if (result.valid) {
          setFirstName(result.first_name || '');
          setEmail(result.email || '');
          setPhase('ready');
        } else {
          setMessage(result.error || 'This reset link is invalid or has expired.');
          setPhase('invalid');
        }
      } catch (err: unknown) {
        if (!active) return;
        const detail =
          (axios.isAxiosError(err) && (err.response?.data as { error?: string } | undefined)?.error) ||
          'This reset link is invalid or has expired.';
        setMessage(detail);
        setPhase('invalid');
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setPhase('submitting');
    try {
      const result = await authAPI.confirmPasswordReset(token, password);
      setMessage(result.message);
      setPhase('done');
    } catch (err: unknown) {
      let detail = 'Something went wrong resetting your password. Please try again.';
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { error?: string; password?: string[] } | undefined;
        detail = data?.error || data?.password?.[0] || detail;
      }
      setError(detail);
      setPhase('ready');
    }
  };

  return (
    <div className="standalone-screen flex justify-center p-4">
      <div className="w-full max-w-md m-auto">
        <div className="text-center mb-8">
          <BrandLogo className="h-28 w-28 mx-auto" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {phase === 'verifying' && (
            <div className="flex flex-col items-center py-8 text-center">
              <Loader2 className="w-10 h-10 text-navy animate-spin mb-4" />
              <p className="text-gray-600 text-base">Checking your reset link...</p>
            </div>
          )}

          {phase === 'invalid' && (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-4">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-navy mb-2">Link unavailable</h2>
              <p className="text-gray-600 text-base mb-6">{message}</p>
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-navy text-white py-3 rounded-lg font-semibold text-base hover:bg-navy-dark transition"
              >
                Request a new link
              </button>
            </div>
          )}

          {(phase === 'ready' || phase === 'submitting' || phase === 'done') && (
            <>
              <h2 className="text-xl font-semibold text-navy mb-1 text-center">
                {phase === 'done' ? 'Password reset' : firstName ? `Hi, ${firstName}` : 'Choose a new password'}
              </h2>
              <p className="text-gray-600 text-base mb-6 text-center">
                {phase === 'done'
                  ? message
                  : <>Set a new password{email ? <> for <span className="font-medium text-navy">{email}</span></> : ''}.</>}
              </p>

              {phase === 'done' ? (
                <button
                  onClick={() => navigate('/login', { replace: true })}
                  className="w-full bg-navy text-white py-3 rounded-lg font-semibold text-base hover:bg-navy-dark transition flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Go to sign in
                </button>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition text-base"
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition text-base"
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      required
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={phase === 'submitting'}
                    className="w-full bg-navy text-white py-3 rounded-lg font-semibold text-base hover:bg-navy-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {phase === 'submitting' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Resetting password...
                      </>
                    ) : (
                      'Reset password'
                    )}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
